/**
 * Production section (F0, `garment.wip`) — the size-wise shop-floor brigade on
 * the Style File cockpit. Replaces the flat 4-stage capture with the real chain
 * (cutting → stitching → khaja → washing → ironing → qc → packing): each stage's
 * received → output → loss → balance, carried size by size. A loss must carry a
 * reason (principle 3). Finished goods = QC-passed output; by-products tracked
 * separately so the order reconciles.
 *
 * @see packages/domain/src/garment/wip
 */
import { useState } from "react";
import { Activity } from "lucide-react";
import { Button, Input, toast } from "@flint/ui";
import type { wip as wipContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";
import type { StyleStatus } from "../lib/status.js";
import { FormSelect } from "./form-select.js";
import { CutPlanCard } from "./cut-plan-card.js";
import { ProductionPlanCard } from "./production-plan-card.js";

type WipData = wipContract.StyleWipViewWire;
type WipStage = WipData["stages"][number]["stage"];
type WipKind = "in" | "out" | "loss";

const KIND_OPTIONS: { value: WipKind; label: string }[] = [
  { value: "out", label: "Output (good)" },
  { value: "loss", label: "Loss (shortfall)" },
  { value: "in", label: "Received" },
];

interface WipSectionProps {
  styleId: string;
  styleStatus: StyleStatus;
}

export function WipSection({ styleId, styleStatus }: WipSectionProps) {
  const utils = trpc.useUtils();
  const query = trpc.wip.forStyle.useQuery({ styleId });
  const [stage, setStage] = useState<WipStage>("cutting");
  const [size, setSize] = useState("");
  const [kind, setKind] = useState<WipKind>("out");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [lineRef, setLineRef] = useState("");
  const [entryDate, setEntryDate] = useState(() => new Date().toISOString().slice(0, 10));

  const record = trpc.wip.record.useMutation({
    onSuccess: async () => {
      setQty("");
      setReason("");
      // Invalidate the whole `wip.*` sub-tree: one movement ripples into every
      // downstream stage row (self-linked received), the Grid lens, the
      // finished-goods board (when it's QC output), and per-style balances.
      // Also `styles` (status may flip pp_approved → in_production) and audit.
      await Promise.all([
        utils.wip.invalidate(),
        utils.styles.get.invalidate({ id: styleId }),
        utils.styles.list.invalidate(),
        utils.auditLog.list.invalidate(),
      ]);
      toast.success("Floor movement recorded");
    },
    onError: (e: { message: string }) => toast.error("Couldn't record", { description: e.message }),
  });

  if (
    styleStatus !== "pp_approved" &&
    styleStatus !== "in_production" &&
    styleStatus !== "shipped" &&
    styleStatus !== "closed"
  ) {
    return (
      <div className="rounded-sm border border-dashed border-line bg-bg px-4 py-6 text-center">
        <p className="text-sm font-medium text-ink-2">Production starts after PP approval</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted">
          Once the style is PP-approved and on the floor, the size-wise cutting → stitching →
          finishing → QC brigade is captured here.
        </p>
      </div>
    );
  }

  if (query.isLoading) return <p className="text-sm text-muted">Loading production…</p>;
  const data = query.data;
  if (!data) return <p className="text-sm text-muted">No production data.</p>;

  const sizes = data.sizes;
  const activeSize = size || sizes[0] || "";
  const canRecord = styleStatus === "pp_approved" || styleStatus === "in_production";
  const lossNeedsReason = kind === "loss" && reason.trim() === "";
  const selectedStage = data.stages.find((s) => s.stage === stage);

  return (
    <div className="flex flex-col gap-4">
      {/* Production plan — unit / supervisor / target / daily pace vs actual. */}
      <ProductionPlanCard styleId={styleId} orderQty={data.orderQty} finishedTotal={data.finishedTotal} />

      {/* Cutting reconciliation — issued fabric ÷ per-piece standard vs actual cut. */}
      <CutPlanCard styleId={styleId} />

      {/* Brigade — per-stage received → output → loss → balance (totals). */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
              <th className="py-2 pr-3 text-left font-medium">Stage</th>
              <th className="px-2 py-2 text-right font-medium">Received</th>
              <th className="px-2 py-2 text-right font-medium">Output</th>
              <th className="px-2 py-2 text-right font-medium">Loss</th>
              <th className="px-2 py-2 text-right font-medium">Balance</th>
            </tr>
          </thead>
          <tbody>
            {data.stages.map((s) => {
              const active = s.received !== 0 || s.output !== 0 || s.loss !== 0;
              return (
                <tr key={s.stage} className={`border-b border-line ${active ? "" : "opacity-45"}`}>
                  <td className="py-2 pr-3 font-medium text-ink">{s.label}</td>
                  <td className="px-2 py-2 text-right font-mono text-ink-2">{s.received.toLocaleString("en-IN")}</td>
                  <td className="px-2 py-2 text-right font-mono text-ink-2">{s.output.toLocaleString("en-IN")}</td>
                  <td className={`px-2 py-2 text-right font-mono ${s.loss > 0 ? "text-bad" : "text-muted"}`}>
                    {s.loss > 0 ? s.loss.toLocaleString("en-IN") : "—"}
                  </td>
                  <td
                    className={`px-2 py-2 text-right font-mono font-semibold ${
                      s.balance > 0 ? "text-warn" : s.balance < 0 ? "text-bad" : "text-ink"
                    }`}
                  >
                    {s.balance.toLocaleString("en-IN")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Finished goods (QC-passed) + by-products. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-md border border-emerald-100 bg-emerald-50 p-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-ok">Finished goods · QC-passed</div>
          <div className="mt-1 font-mono text-xl font-bold text-ok">{data.finishedTotal.toLocaleString("en-IN")} pcs</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs text-ink-2">
            {data.finishedGoods.some((f) => f.qty > 0) ? (
              data.finishedGoods
                .filter((f) => f.qty > 0)
                .map((f) => (
                  <span key={f.size} className="rounded-full bg-paper px-2 py-0.5 font-medium">
                    {f.size} · {f.qty.toLocaleString("en-IN")}
                  </span>
                ))
            ) : (
              <span className="text-muted">None yet — passes QC to land here.</span>
            )}
          </div>
        </div>
        <div className="rounded-md border border-line bg-bg p-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted">Cut-pieces · by-products</div>
          {data.cutPieces.length ? (
            <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs text-ink-2">
              {data.cutPieces.map((c) => (
                <span key={`${c.kind}-${c.uom}`} className="rounded-full border border-line bg-paper px-2 py-0.5">
                  {c.kind.replace(/_/g, " ")}: {c.qty.toLocaleString("en-IN")} {c.uom}
                </span>
              ))}
            </div>
          ) : (
            <div className="mt-1.5 text-xs text-muted">None recorded.</div>
          )}
        </div>
      </div>

      {/* Record a movement. */}
      {canRecord ? (
        <div className="rounded-md border border-line bg-bg p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Record floor movement</h3>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Stage">
              <FormSelect
                ariaLabel="Stage"
                value={stage}
                onValueChange={(v) => setStage(v as WipStage)}
                options={data.stages.map((s) => ({ value: s.stage, label: s.label }))}
                triggerClassName="h-8 w-32 px-2 text-xs"
              />
            </Field>
            <Field label="Size">
              <FormSelect
                ariaLabel="Size"
                value={activeSize}
                onValueChange={setSize}
                options={sizes.map((z) => ({ value: z, label: z }))}
                triggerClassName="h-8 w-20 px-2 text-xs"
              />
            </Field>
            <Field label="Type">
              <FormSelect
                ariaLabel="Type"
                value={kind}
                onValueChange={(v) => setKind(v as WipKind)}
                options={KIND_OPTIONS}
                triggerClassName="h-8 w-36 px-2 text-xs"
              />
            </Field>
            <Field label="Qty (pcs)">
              <Input
                value={qty}
                onChange={(e) => setQty(e.currentTarget.value)}
                placeholder="98"
                inputMode="numeric"
                className="h-8 w-20 text-xs"
              />
            </Field>
            {kind === "loss" ? (
              <Field label="Reason (required)">
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.currentTarget.value)}
                  placeholder="machine breakdown"
                  className="h-8 w-44 text-xs"
                />
              </Field>
            ) : null}
            <Field label="Line">
              <Input
                value={lineRef}
                onChange={(e) => setLineRef(e.currentTarget.value)}
                placeholder="Line 4"
                className="h-8 w-20 text-xs"
              />
            </Field>
            <Field label="Date">
              <input
                type="date"
                value={entryDate}
                onChange={(e) => setEntryDate(e.currentTarget.value)}
                className="h-8 rounded-sm border border-line bg-paper px-2 font-mono text-xs text-ink outline-none focus-visible:border-sky-600"
              />
            </Field>
            <Button
              size="sm"
              disabled={record.isPending || lossNeedsReason}
              onClick={() => {
                const n = Math.round(Number(qty));
                if (!Number.isFinite(n) || n === 0) {
                  toast.error("Enter a quantity");
                  return;
                }
                if (!activeSize) {
                  toast.error("Pick a size");
                  return;
                }
                record.mutate({
                  styleId,
                  stage,
                  size: activeSize,
                  kind,
                  qty: n,
                  reason: reason.trim() || null,
                  lineRef: lineRef.trim() || null,
                  entryDate,
                });
              }}
            >
              <Activity size={14} /> Record
            </Button>
          </div>
          {lossNeedsReason ? (
            <p className="mt-2 text-xs font-medium text-warn">A loss needs a reason before you can record it.</p>
          ) : null}
          {selectedStage ? (
            <>
              <p className="mt-2 text-xs text-muted">
                {selectedStage.label}: received {selectedStage.received.toLocaleString("en-IN")} · output{" "}
                {selectedStage.output.toLocaleString("en-IN")} · balance{" "}
                <b className={selectedStage.balance === 0 ? "text-ink" : "text-warn"}>
                  {selectedStage.balance.toLocaleString("en-IN")}
                </b>
              </p>
              {selectedStage.sizes.length > 0 ? (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
                  <span className="font-semibold uppercase tracking-wider text-muted">Sizes</span>
                  {selectedStage.sizes.map((s) => {
                    const isSelected = s.size === activeSize;
                    const tone = s.balance < 0 ? "text-bad" : s.balance > 0 ? "text-warn" : "text-muted";
                    return (
                      <span
                        key={s.size}
                        className={
                          isSelected
                            ? "rounded-full border border-sky-500 bg-sky-50 px-2 py-0.5 font-medium text-ink"
                            : "rounded-full border border-line bg-paper px-2 py-0.5 text-ink-2"
                        }
                      >
                        {s.size} <span className={`ml-1 font-mono ${tone}`}>{s.balance.toLocaleString("en-IN")}</span>
                      </span>
                    );
                  })}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">{label}</span>
      {children}
    </label>
  );
}
